<template>
    <!-- Empty tag to make ssr rehydration happy — it complains if the template is empty -->
    <div data-head-meta />
</template>
<script setup lang="ts">
import { type ReactiveHead, useHead } from '@unhead/vue'
import { useMounted } from '@vueuse/core'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// without '?file' webpack tries to inline into base64, since the files are relatively small
import BestWebGamesFavicon512 from '@/assets/icons/best-web-games-favicon-512.forced.png?file'
import PGFavicon32 from '@/assets/icons/favicon-32.forced.png?file'
import PGFavicon48 from '@/assets/icons/favicon-48.forced.png?file'
import PGFavicon96 from '@/assets/icons/favicon-96.forced.png?file'
import PGFavicon144 from '@/assets/icons/favicon-144.forced.png?file'
import PGFavicon180 from '@/assets/icons/favicon-180.forced.png?file'
import PGFavicon192 from '@/assets/icons/favicon-192.forced.png?file'
import PGFavicon512 from '@/assets/icons/favicon-512.forced.png?file'
import PGFaviconSVG from '@/assets/icons/favicon.svg?file'
import PlaygamaOG from '@/assets/icons/Playgama-OG.webp?file'
import { useConfig } from '@/composables/config'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { useTr } from '@/composables/use-tr'
import { GAM_SOURCE } from '@/constants/ads'
import { PWA_APP_CLIDS, MANIFEST_META_KEY } from '@/constants/general'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useGoogleAuthStore } from '@/store/google-auth-store'
import { usePushSubscriptionStore } from '@/store/push-subscribe/store'

const iconsMap = {
    PGFavicon32,
    PGFavicon48,
    PGFavicon96,
    PGFavicon144,
    PGFavicon180,
    PGFavicon192,
    PGFavicon512,
    PGFaviconSVG,
}

const props = defineProps<{ prepareGoogleAds: boolean }>()

const tr = useTr()
const pushSubscriptionStore = usePushSubscriptionStore()
const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const isMounted = useMounted()
const { mainData } = useGeneralData()
const isAppInstallBannerDisabled = computed(() => !!route.meta.noAppInstallBanner)

const googleAuthStore = useGoogleAuthStore()

const adaptiveIcons = (() => {
    const sizes = [
        ['SVG', 'image/svg+xml'],
        [32, 'image/png'],
        [48, 'image/png'],
        [96, 'image/png'],
        [144, 'image/png'],
        [180, 'image/png'],
        [192, 'image/png'],
        [512, 'image/png'],
    ] as const
    const res: { rel: string; type: string; sizes?: string; href: string }[] = []
    sizes.forEach(([size, type]) => {
        const newSizes = typeof size === 'number' ? `${size}x${size}` : undefined
        res.push({
            rel: 'icon',
            type,
            sizes: newSizes,
            href: iconsMap[`PGFavicon${size}`],
        })
    })
    return res
})()

const {
    VUE_APP_HOST_URL,
    ROBOTS_DISALLOW,
    VUE_APP_AD_MANAGER_ACCOUNT,
    VUE_APP_IOS_APP_ID,
    VUE_APP_GTM_ID,
    VUE_APP_SGTM_HOST,
} = useConfig()

const pageScripts = computed(() => {
    const scripts: ReactiveHead['script'] = ['window.dataLayer = window.dataLayer || [];']
    if (props.prepareGoogleAds && VUE_APP_GTM_ID) {
        const gtmHost = VUE_APP_SGTM_HOST || 'https://www.googletagmanager.com'
        scripts.push(`window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'})`, {
            src: `${gtmHost}/gtm.js?id=${VUE_APP_GTM_ID}`,
            async: true,
            fetchpriority: 'high',
        })
    }
    if (isMounted.value) {
        scripts.push({
            src: 'https://accounts.google.com/gsi/client',
            async: true,
            onload: googleAuthStore.initGlobalSignIn,
        })

        if (pushSubscriptionStore.properEnv) {
            scripts.push({
                src: 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js',
                defer: true,
            })
        }
    }

    return scripts
})

const pageMeta = computed(() => {
    const meta: ReactiveHead['meta'] = [
        {
            name: 'description',
            content: tr('headMeta.description'),
        },
        {
            name: 'validation-token',
            content: 'hb42hn0e5uyz0bfp',
        },
        // disable explicit as it makes iOS safari to render white UI around export frame
        // {
        //     name: 'color-scheme',
        //     content: 'dark',
        // },

        // Progressive Web App
        {
            name: 'mobile-web-app-capable',
            content: 'yes',
        },
        {
            name: 'apple-mobile-web-app-capable',
            content: 'yes',
        },
        {
            name: 'apple-mobile-web-app-status-bar-style',
            content: 'black-translucent',
        },
        {
            name: 'theme-color',
            content: '#000000',
        },
        // temporary OG image
        // TODO better OG card
        {
            property: 'og:image',
            content: PlaygamaOG,
        },
        {
            property: 'og:image:alt',
            content: tr('headMeta.alt_image'),
        },
        {
            property: 'og:title',
            content: tr('headMeta.title'),
        },
        {
            property: 'og:description',
            content: tr('headMeta.description'),
        },
        {
            property: 'og:type',
            content: 'website',
        },
        {
            property: 'og:url',
            content: `${VUE_APP_HOST_URL}${route.fullPath === '/' ? '' : route.fullPath}`,
        },
        {
            property: 'og:site_name',
            content: 'Playgama.com',
        },
        {
            name: 'twitter:card',
            content: 'summary',
        },
        {
            name: 'twitter:title',
            content: tr('headMeta.title'),
        },
        {
            name: 'twitter:description',
            content: tr('headMeta.description'),
        },
        {
            name: 'twitter:image',
            content: PlaygamaOG,
        },
        {
            name: 'twitter:image:alt',
            content: tr('headMeta.alt_image'),
        },
        {
            name: 'apple-mobile-web-app-title',
            content: 'Playgama',
            key: 'apple-mobile-web-app-title',
        },
    ]

    // For install iOS App (Smart App Banner in Safari)
    if (!isAppInstallBannerDisabled.value) {
        meta.push({
            name: 'google-play-app',
            content: 'app-id=com.playgama.twa',
        })
    }

    if (VUE_APP_IOS_APP_ID && !isAppInstallBannerDisabled.value) {
        meta.push({
            name: 'apple-itunes-app',
            content: `app-id=${VUE_APP_IOS_APP_ID}`,
        })
    }

    if (ROBOTS_DISALLOW === 'DISALLOW') {
        meta.push({
            name: 'robots',
            content: 'noindex',
        })
    }
    return meta
})

const { getCanonicalAndAlternates } = useLocalizedRouters()
const $route = useRoute()

const pageLink = computed(() => {
    const pwaStartURL = `${VUE_APP_HOST_URL}${router.resolve({ name: ROUTE_NAMES.MAIN, query: { clid: PWA_APP_CLIDS.pwa } }).fullPath}`
    const links: ReactiveHead['link'] = [
        ...(!$route.meta.noRootManifest
            ? [
                  {
                      rel: 'manifest',
                      'data-start_url': pwaStartURL,
                      href: `/manifest/main.webmanifest?clid=${PWA_APP_CLIDS.pwa}`,
                      key: MANIFEST_META_KEY,
                  },
              ]
            : []),

        {
            rel: 'icon',
            href: '/favicon.ico',
            sizes: '32x32',
        },
        ...adaptiveIcons,
        {
            rel: 'apple-touch-icon',
            sizes: '180x180',
            href: PGFavicon180,
            key: 'apple-touch-icon',
        },
        ...getCanonicalAndAlternates(),
    ]

    if (props.prepareGoogleAds) {
        links.push(
            {
                rel: 'preload',
                'data-early-hints': true,
                href: GAM_SOURCE,
                as: 'script',
                crossorigin: 'anonymous',
            },
            {
                rel: 'preload',
                'data-early-hints': true,
                href: `https://fundingchoicesmessages.google.com/i/${VUE_APP_AD_MANAGER_ACCOUNT}?ers=3`,
                as: 'script',
                // funding choice does not support CORS
                crossorigin: undefined,
            },
        )
    }

    return links
})

const headTitle = computed(() => {
    if (mainData.value.totalGamesCount) {
        return `${tr('headMeta.title')} ${tr('headMeta.titleCount', { count: mainData.value.totalGamesCount.toString() }, mainData.value.totalGamesCount)}`
    }
    return tr('headMeta.title')
})

const pageHead = computed(() => {
    if (appStore.isOppoWL) {
        return {
            title: 'Best web games',
            link: [
                {
                    rel: 'icon',
                    type: 'image/png',
                    href: BestWebGamesFavicon512,
                },
            ],
        }
    }
    return {
        title: headTitle.value,
        meta: pageMeta.value,
        link: pageLink.value,
        script: pageScripts.value,
    }
})
useHead(pageHead)
</script>
